import type { FC } from 'react';
import type { SvgIconProps } from '@material-ui/core/SvgIcon';

import { BAB } from 'src/assets/icons/generationTools/BAB';
import { BlogIdeas } from 'src/assets/icons/generationTools/BlogIdeas';
import { F2B } from 'src/assets/icons/generationTools/F2B';
import { IconAIDA } from 'src/assets/icons/generationTools/IconAIDA';
import { IconAmazon } from 'src/assets/icons/generationTools/IconAmazon';
import { IconCampaigns } from 'src/assets/icons/generationTools/IconCampaigns';
import { IconCompanyBio } from 'src/assets/icons/generationTools/IconCompanyBio';
import { IconContentImprover } from 'src/assets/icons/generationTools/IconContentImprover';
import { IconCustomMultiPrompt } from 'src/assets/icons/generationTools/IconCustomMultiPrompt';
import { IconEli5 } from 'src/assets/icons/generationTools/IconEli5';
import { IconEmailTool } from 'src/assets/icons/generationTools/IconEmailTool';
import { IconEngagingQuestions } from 'src/assets/icons/generationTools/IconEngagingQuestions';
import { IconFacebook } from 'src/assets/icons/generationTools/IconFacebook';
import { IconFreestyleTool } from 'src/assets/icons/generationTools/IconFreestyleTool';
import { IconGeneralPrompt } from 'src/assets/icons/generationTools/IconGeneralPrompt';
import { IconGoogle } from 'src/assets/icons/generationTools/IconGoogle';
import { IconHomePage } from 'src/assets/icons/generationTools/IconHomePage';
import { IconImage } from 'src/assets/icons/generationTools/IconImage';
import { IconInstagram } from 'src/assets/icons/generationTools/IconInstagram';
import { IconLandingPage } from 'src/assets/icons/generationTools/IconLandingPage';
import { IconLandingPageSeo } from 'src/assets/icons/generationTools/IconLandingPageSeo';
import { IconLinkedin } from 'src/assets/icons/generationTools/IconLinkedin';
import { IconMeta } from 'src/assets/icons/generationTools/IconMeta';
import { IconOutbrain } from 'src/assets/icons/generationTools/IconOutbrain';
import { IconPersonalBio } from 'src/assets/icons/generationTools/IconPersonalBio';
import { IconPinterest } from 'src/assets/icons/generationTools/IconPinterest';
import { IconProductReview } from 'src/assets/icons/generationTools/IconProductReview';
import { IconQuoraAnswers } from 'src/assets/icons/generationTools/IconQuoraAnswers';
import { IconSentenceExpander } from 'src/assets/icons/generationTools/IconSentenceExpander';
import { IconSms } from 'src/assets/icons/generationTools/IconSms';
import { IconSummarize } from 'src/assets/icons/generationTools/IconSummarize';
import { IconTaboola } from 'src/assets/icons/generationTools/IconTaboola';
import { IconTalkingPoints } from 'src/assets/icons/generationTools/IconTalkingPoints';
import { IconTeads } from 'src/assets/icons/generationTools/IconTeads';
import { IconUnknown } from 'src/assets/icons/generationTools/IconUnknown';
import { IconWalmart } from 'src/assets/icons/generationTools/IconWalmart';
import { IconX } from 'src/assets/icons/generationTools/IconX';
import { IconYoutube } from 'src/assets/icons/generationTools/IconYoutube';
import { PAS } from 'src/assets/icons/generationTools/PAS';
import { PerfectHeadline } from 'src/assets/icons/generationTools/PerfectHeadline';
import { PowerfulBullets } from 'src/assets/icons/generationTools/PowerfulBullets';
import { ProductDescriptionList } from 'src/assets/icons/generationTools/ProductDescriptionList';
import { ProductDescriptionParagraph } from 'src/assets/icons/generationTools/ProductDescriptionParagraph';
import { ValueProp } from 'src/assets/icons/generationTools/ValueProp';
import { VideoTopicIdeas } from 'src/assets/icons/generationTools/VideoTopicIdeas';
import { IconWTMBlack } from 'src/assets/icons/IconWTMBlack';
import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';

const ICONS: Partial<Record<GenerationTool, FC<SvgIconProps>>> = {
  // "basic" and "basic DD"
  [GENERATION_TOOL.basicContentImprover]: IconContentImprover,
  [GENERATION_TOOL.basicEli5]: IconEli5,
  [GENERATION_TOOL.basicSentenceExpander]: IconSentenceExpander,
  [GENERATION_TOOL.basicAida]: IconAIDA,
  [GENERATION_TOOL.basicPas]: PAS,
  [GENERATION_TOOL.basicBab]: BAB,
  [GENERATION_TOOL.basicFeatureToBenefitsPractical]: F2B,
  [GENERATION_TOOL.basicPerfectHeadline]: PerfectHeadline,
  [GENERATION_TOOL.basicPersuasiveBulletPoint]: PowerfulBullets,
  [GENERATION_TOOL.basicValueProposition]: ValueProp,
  [GENERATION_TOOL.basicProductDescriptionList]: ProductDescriptionList,
  [GENERATION_TOOL.basicProductDescriptionRelaxed]: ProductDescriptionParagraph,
  [GENERATION_TOOL.basicBlogIdeas]: BlogIdeas,
  [GENERATION_TOOL.basicVideoIdeas]: VideoTopicIdeas,
  [GENERATION_TOOL.basicTalkingPointsToParagraph]: IconTalkingPoints,
  [GENERATION_TOOL.basicProductReview]: IconProductReview,
  [GENERATION_TOOL.summarizer]: IconSummarize,
  [GENERATION_TOOL.basicPersonalBio]: IconPersonalBio,
  [GENERATION_TOOL.basicCompanyBio]: IconCompanyBio,
  [GENERATION_TOOL.basicEngagingQuestions]: IconEngagingQuestions,
  [GENERATION_TOOL.basicQuoraAnswers]: IconQuoraAnswers,
  [GENERATION_TOOL.generalPrompt]: IconGeneralPrompt,

  // "data driven"
  [GENERATION_TOOL.facebookMixtureAd]: IconMeta,
  [GENERATION_TOOL.facebookPrimaryTextProduct]: IconMeta,
  [GENERATION_TOOL.facebookPrimaryTextProductRewrite]: IconMeta,
  [GENERATION_TOOL.facebookHeadlineProduct]: IconMeta,
  [GENERATION_TOOL.facebookHeadlineProductRewrite]: IconMeta,
  [GENERATION_TOOL.googleAppAd]: IconGoogle,
  [GENERATION_TOOL.googleDemandGenAd]: IconGoogle,
  [GENERATION_TOOL.googleDisplayAd]: IconGoogle,
  [GENERATION_TOOL.googleShoppingAd]: IconGoogle,
  [GENERATION_TOOL.googleVideoAd]: IconGoogle,
  [GENERATION_TOOL.googleRSA]: IconGoogle,
  [GENERATION_TOOL.googleSearchAdHeadlineProduct]: IconGoogle,
  [GENERATION_TOOL.googleSearchAdDescriptionProduct]: IconGoogle,
  [GENERATION_TOOL.googleDescriptionProduct]: IconGoogle,
  [GENERATION_TOOL.googleShortDescriptionProduct]: IconGoogle,
  [GENERATION_TOOL.googleHeadlineProductRewrite]: IconGoogle,
  [GENERATION_TOOL.googlePerformanceMaxAd]: IconGoogle,
  [GENERATION_TOOL.googleDescriptionProductRewrite]: IconGoogle,
  [GENERATION_TOOL.googleHeadlineProduct]: IconGoogle,
  [GENERATION_TOOL.googleMediumHeadlineProduct]: IconGoogle,
  [GENERATION_TOOL.googleLongHeadlineProduct]: IconGoogle,
  [GENERATION_TOOL.googleDemandGenAdLongHeadlineProduct]: IconGoogle,
  [GENERATION_TOOL.googleDemandGenAdDescriptionProduct]: IconGoogle,
  [GENERATION_TOOL.googleDisplayAdHeadlineProduct]: IconGoogle,
  [GENERATION_TOOL.googleDisplayAdLongHeadlineProduct]: IconGoogle,
  [GENERATION_TOOL.googleDisplayAdDescriptionProduct]: IconGoogle,
  [GENERATION_TOOL.googleShoppingAdTitle]: IconGoogle,
  [GENERATION_TOOL.googleShoppingAdDescription]: IconGoogle,
  [GENERATION_TOOL.googleVideoAdHeadlineProduct]: IconGoogle,
  [GENERATION_TOOL.googleVideoAdLongHeadlineProduct]: IconGoogle,
  [GENERATION_TOOL.googleVideoAdDescriptionProduct]: IconGoogle,
  [GENERATION_TOOL.googleAppAdHeadlineProduct]: IconGoogle,
  [GENERATION_TOOL.googleAppAdDescriptionProduct]: IconGoogle,
  [GENERATION_TOOL.outbrainAd]: IconOutbrain,
  [GENERATION_TOOL.outbrainHeadlineProduct]: IconOutbrain,
  [GENERATION_TOOL.outbrainDescriptionProduct]: IconOutbrain,
  [GENERATION_TOOL.outbrainHeadlineProductRewrite]: IconOutbrain,
  [GENERATION_TOOL.taboolaAd]: IconTaboola,
  [GENERATION_TOOL.taboolaHeadlineProduct]: IconTaboola,
  [GENERATION_TOOL.taboolaDescriptionProduct]: IconTaboola,
  [GENERATION_TOOL.taboolaHeadlineProductRewrite]: IconTaboola,
  [GENERATION_TOOL.twitterMixtureAd]: IconX,
  [GENERATION_TOOL.twitterCopyProduct]: IconX,
  [GENERATION_TOOL.twitterCopyProductRewrite]: IconX,
  [GENERATION_TOOL.twitterTitleProduct]: IconX,
  [GENERATION_TOOL.imageWithDescription]: IconImage,
  [GENERATION_TOOL.imageByStyle]: IconImage,
  [GENERATION_TOOL.facebookPostImage]: IconFacebook,
  [GENERATION_TOOL.linkedinPostImage]: IconLinkedin,
  [GENERATION_TOOL.instagramPostImage]: IconInstagram,
  [GENERATION_TOOL.pinterestPinImage]: IconPinterest,
  [GENERATION_TOOL.xPostImage]: IconX,
  [GENERATION_TOOL.youtubeVideoImage]: IconYoutube,
  [GENERATION_TOOL.metaAdImage]: IconMeta,
  [GENERATION_TOOL.linkedinSingleImageAdImage]: IconLinkedin,
  [GENERATION_TOOL.tweeterAdImage]: IconX,
  [GENERATION_TOOL.pinterestAdImage]: IconPinterest,
  [GENERATION_TOOL.googleShoppingAdImage]: IconGoogle,
  [GENERATION_TOOL.googleAppAdImage]: IconGoogle,
  [GENERATION_TOOL.outbrainAdImage]: IconOutbrain,
  [GENERATION_TOOL.taboolaAdImage]: IconTaboola,
  [GENERATION_TOOL.imageImprove]: IconImage,
  [GENERATION_TOOL.emailImage]: IconEmailTool,
  [GENERATION_TOOL.pinterestMixtureAd]: IconPinterest,
  [GENERATION_TOOL.pinterestDescriptionProduct]: IconPinterest,
  [GENERATION_TOOL.pinterestDescriptionProductRewrite]: IconPinterest,
  [GENERATION_TOOL.pinterestTitleProduct]: IconPinterest,
  [GENERATION_TOOL.landingHeadline]: IconLandingPage,
  [GENERATION_TOOL.landingHeadlineRewrite]: IconLandingPage,
  [GENERATION_TOOL.landingSubheader]: IconLandingPage,
  [GENERATION_TOOL.landingSubheaderRewrite]: IconLandingPage,
  [GENERATION_TOOL.landingCTA]: IconLandingPage,
  [GENERATION_TOOL.landingBenefits]: IconLandingPage,
  [GENERATION_TOOL.webpageGeneralText]: IconLandingPage,
  [GENERATION_TOOL.landingFeatures]: IconLandingPage,
  [GENERATION_TOOL.landingSectionHeadline]: IconLandingPage,
  [GENERATION_TOOL.landingSEOMetadata]: IconLandingPageSeo,
  [GENERATION_TOOL.landingSEOMetaTitle]: IconLandingPageSeo,
  [GENERATION_TOOL.landingSEOMetadataRewrite]: IconLandingPageSeo,
  [GENERATION_TOOL.amazonProductListing]: IconAmazon,
  [GENERATION_TOOL.walmartProductListing]: IconWalmart,
  [GENERATION_TOOL.instagramCaption]: IconInstagram,
  [GENERATION_TOOL.facebookPost]: IconFacebook,
  [GENERATION_TOOL.tweet]: IconX,
  [GENERATION_TOOL.linkedinPost]: IconLinkedin,
  [GENERATION_TOOL.linkedinPostRewrite]: IconLinkedin,
  [GENERATION_TOOL.pinterestMixturePost]: IconPinterest,
  [GENERATION_TOOL.youtubeMixturePost]: IconYoutube,
  [GENERATION_TOOL.pinterestPostDescription]: IconPinterest,
  [GENERATION_TOOL.pinterestPostTitle]: IconPinterest,
  [GENERATION_TOOL.youtubeTitle]: IconYoutube,
  [GENERATION_TOOL.youtubeDescription]: IconYoutube,
  [GENERATION_TOOL.emailSubjectLine]: IconEmailTool,
  [GENERATION_TOOL.emailGeneralText]: IconEmailTool,
  [GENERATION_TOOL.emailPromotionalSubjectLine]: IconEmailTool,
  [GENERATION_TOOL.emailPromotionalBody]: IconEmailTool,
  [GENERATION_TOOL.emailColdOutreachSubjectLine]: IconEmailTool,
  [GENERATION_TOOL.emailColdOutreachBody]: IconEmailTool,
  [GENERATION_TOOL.emailColdOutreachBodyRewrite]: IconEmailTool,
  [GENERATION_TOOL.emailPromotionalBodyRewrite]: IconEmailTool,
  [GENERATION_TOOL.emailBodyRewrite]: IconEmailTool,
  [GENERATION_TOOL.emailSubject]: IconEmailTool,
  [GENERATION_TOOL.emailBody]: IconEmailTool,
  [GENERATION_TOOL.emailMixture]: IconEmailTool,
  [GENERATION_TOOL.emailHeadline]: IconEmailTool,
  [GENERATION_TOOL.emailCta]: IconEmailTool,
  [GENERATION_TOOL.emailSectionHeadline]: IconEmailTool,
  [GENERATION_TOOL.emailHeader]: IconEmailTool,
  [GENERATION_TOOL.landingHeader]: IconLandingPage,
  [GENERATION_TOOL.emailIntroParagraph]: IconEmailTool,
  [GENERATION_TOOL.emailSubjectRewrite]: IconEmailTool,
  [GENERATION_TOOL.smsPromotional]: IconSms,
  [GENERATION_TOOL.smsPromotionalRewrite]: IconSms,
  [GENERATION_TOOL.facebookPrimaryTextContent]: IconFacebook,
  [GENERATION_TOOL.facebookHeadlineContent]: IconFacebook,
  [GENERATION_TOOL.outbrainHeadlineContent]: IconOutbrain,
  [GENERATION_TOOL.taboolaHeadlineContent]: IconTaboola,
  [GENERATION_TOOL.twitterCopyContent]: IconX,
  [GENERATION_TOOL.pinterestDescriptionContent]: IconPinterest,
  [GENERATION_TOOL.pinterestTitleContent]: IconPinterest,
  [GENERATION_TOOL.teadsSingleImageHeadline]: IconTeads,
  [GENERATION_TOOL.teadsSingleImageDescription]: IconTeads,
  [GENERATION_TOOL.teadsSingleVideoHeadline]: IconTeads,
  [GENERATION_TOOL.teadsSingleVideoDescription]: IconTeads,
  [GENERATION_TOOL.teadsCarouselHeadline]: IconTeads,
  [GENERATION_TOOL.teadsSocialLayout1Headline]: IconTeads,
  [GENERATION_TOOL.teadsSocialLayout1PrimaryText]: IconTeads,
  [GENERATION_TOOL.teadsSocialLayout1Description]: IconTeads,
  [GENERATION_TOOL.teadsSocialLayout2PrimaryText]: IconTeads,
  [GENERATION_TOOL.generalProductListing]: ProductDescriptionParagraph,
  [GENERATION_TOOL.linkedinHeadlineSingleImageRewrite]: IconLinkedin,
  [GENERATION_TOOL.linkedinIntroSingleImageRewrite]: IconLinkedin,
  [GENERATION_TOOL.linkedinHeadlineProduct]: IconLinkedin,
  [GENERATION_TOOL.linkedinIntroProduct]: IconLinkedin,
  [GENERATION_TOOL.linkedinMessageAdSubject]: IconLinkedin,
  [GENERATION_TOOL.linkedinMessageAdMessage]: IconLinkedin,
  [GENERATION_TOOL.linkedinMessageAdCta]: IconLinkedin,
  [GENERATION_TOOL.linkedinConversationalAdOpeningMessage]: IconLinkedin,
  [GENERATION_TOOL.linkedinConversationalAdPrimaryCtaButtonText]: IconLinkedin,
  [GENERATION_TOOL.linkedinConversationalAdFollowUpMessage]: IconLinkedin,
  [GENERATION_TOOL.linkedinConversationalAdSecondaryCtaButtonText]: IconLinkedin,
  [GENERATION_TOOL.linkedinLeadGenFormAdPrimaryText]: IconLinkedin,
  [GENERATION_TOOL.linkedinLeadGenFormAdHeadline]: IconLinkedin,
  [GENERATION_TOOL.linkedinLeadGenFormAdOfferHeadline]: IconLinkedin,
  [GENERATION_TOOL.linkedinLeadGenFormAdOfferDetails]: IconLinkedin,
  [GENERATION_TOOL.linkedinDocumentAdIntro]: IconLinkedin,
  [GENERATION_TOOL.linkedinDocumentAdHeadline]: IconLinkedin,
  [GENERATION_TOOL.linkedinVideoAdIntro]: IconLinkedin,
  [GENERATION_TOOL.linkedinVideoAdHeadline]: IconLinkedin,
  [GENERATION_TOOL.instagramCaptionRewrite]: IconInstagram,
  [GENERATION_TOOL.facebookPostRewrite]: IconFacebook,
  [GENERATION_TOOL.pinterestPinTitleRewrite]: IconPinterest,
  [GENERATION_TOOL.pinterestPinDescriptionRewrite]: IconPinterest,
  [GENERATION_TOOL.tweetRewrite]: IconX,
  [GENERATION_TOOL.linkedinMixtureAd]: IconLinkedin,
  [GENERATION_TOOL.linkedinDocumentAd]: IconLinkedin,
  [GENERATION_TOOL.linkedinVideoAd]: IconLinkedin,
  [GENERATION_TOOL.linkedinTextAd]: IconLinkedin,
  [GENERATION_TOOL.linkedinMessageAd]: IconLinkedin,
  [GENERATION_TOOL.linkedinConversationAd]: IconLinkedin,
  [GENERATION_TOOL.linkedinLeadGenFormAd]: IconLinkedin,
  [GENERATION_TOOL.linkedinHeadlineSingleImage]: IconLinkedin,
  [GENERATION_TOOL.linkedinIntroSingleImage]: IconLinkedin,
  [GENERATION_TOOL.articleBlogHeadline]: PerfectHeadline,
  [GENERATION_TOOL.productLaunchMultiPrompt]: IconCampaigns,
  [GENERATION_TOOL.landingPageProductAnnouncementMultiPrompt]: IconLandingPage,
  [GENERATION_TOOL.emailDripMultiPrompt]: IconEmailTool,
  [GENERATION_TOOL.customMultiPrompt]: IconCustomMultiPrompt,

  // Freestyle
  [GENERATION_TOOL.freestyle]: IconFreestyleTool,
  [GENERATION_TOOL.createYourOwn]: IconFreestyleTool,

  //project blog
  [GENERATION_TOOL.blogDescription]: PerfectHeadline,

  // project wtm
  [GENERATION_TOOL.wtmDefault]: IconWTMBlack,
  [GENERATION_TOOL.lpcoLandingHeadline]: IconHomePage,
  [GENERATION_TOOL.lpcoLandingCTA]: IconHomePage,
  [GENERATION_TOOL.lpcoLandingProductDescription]: IconHomePage,
  [GENERATION_TOOL.lpcoLandingProductName]: IconHomePage,
  [GENERATION_TOOL.lpcoLandingCopy]: IconHomePage,
};

export const getGenerationToolIcon = (tool: GenerationTool): FC<SvgIconProps> =>
  ICONS[tool] || IconUnknown;
